/* eslint-disable max-lines */
/**
 * @file Timeline component definition and functionality.
 */

import Vue from 'vue';
import { TweenLite } from 'gsap';
import Throttle from 'utils/Throttle';
import { Events } from 'utils/Constants';
import ViewportHelper from 'utils/ViewportHelper';
import 'element-closest/browser';
import 'waypoints/lib/noframework.waypoints.js';

/**
 * Selectors used to query various timeline elements.
 */
const SELECTORS = {
  'ARROW': '.js-timeline-arrow',
  'ARROW_ICON': '.js-timeline-arrow .icon',
  'CARD': '.js-timelineCard:not(.js-timelineCard_modal)',
  'CARD_CONTENT': '.js-timelineCard-content',
  'CARD_FEATURED': '.js-timelineCard_featured',
  'CARD_FEATURED_CONTENT': '.js-timelineCardFeatured-content',
  'CARD_READABLE': '.js-timelineCard-readable',
  'CONTENT': '.js-timeline-content',
  'GROUP': '.js-timeline-group',
  'ITEM': '.js-timeline-item',
  'LEFT_COLUMN': '.js-timeline-column_left',
  'LINKS': '.js-timeline-anchorLinks',
  'RIGHT_COLUMN': '.js-timeline-column_right',
  'QUOTE': '.js-timelineQuote',
  'TIMELINE_CARD': '.js-timeline-card',
  'TIMELINE_YEAR': '.js-timeline-year',
  'YEAR': '.js-timelineYear'
}

/**
 * Classes added, removed, and checked while orchestrating item placement.
 */
const CLASSES = {
  'CARD_FEATURED': 'js-timelineCard_featured',
  'CARD_READABLE': 'js-timelineCard-readable',
  'HIDE': 'js-timeline-hidden',
  'HIDE_VISUALLY': 'js-timeline-hiddenVisually',
  'STICKY': 'js-timeline-sticky',
  'QUOTE': 'js-timelineQuote',
}

/**
 * Attributes that contain information used when scrolling and filtering items.
 */
const ATTRIBUTES = {
  'CATEGORIES': 'data-categories',
  'YEAR': 'data-year'
}

/**
 * Distances used when animating elements in and out of visibilty.
 */
const ANIMATION = {
  'DISTANCE_SM': 30,
  'DISTANCE_MD': 50
}

/**
 * Distances used when placing and spacing items in relation to each other.
 */
const OFFSET = {
  'ITEM': 110,
  'SPACING': 40,
  'YEAR': 128
}

/**
 * Defines the timeline-vue component.
 * @class
 */
Vue.component('timeline-vue', {
  props: {
    // All years that have content within the timeline.
    'years': Array
  },
  data: function() {
    return {
      // The currently active year while navigating the content.
      'activeYear': null,
      // The currently selected category.
      'category': 'All',
      // The years that aren't active while filtering the category.
      'filteredYears': [],
      // Tracks the last page width when the placement was recalculated.
      'lastPageWidth': null,
      // Used to throttle the recalculation of item placement during resize.
      'recalculatingTimer': null,
      // Designates whether the scrolling is a result of using the anchor links.
      'isSmoothScrolling': false,
      // Used to verify the status for the animations.
      'animationsDisabled': false,
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    /**
     * Initializes all interactive behavior.
     */
    init() {
      // Sets the first year as the active year by default.
      this.setFirstYearAsActive();

      // Places the items for the first time.
      this.calculatePlacements();

      // Recalculates the placement of items on resize.
      window.addEventListener(Events.RESIZE, Throttle(e => this.recalculatePlacements(), 50));

      // Check for user preferences in localStorage or reduced motion
      const animationsDisabled = localStorage.getItem('animationsDisabled');

      if (animationsDisabled === 'true') {
        this.animationsDisabled = true;
      }
    },

    // Item placement.

    /**
     * Triggers the recalculation and resulting placement of all items.
     */
    calculatePlacements() {
      // Because calculation is also triggered on resize, the timeout is used to
      // ensure it only happens after a period of inactivity. This prevents
      // unnecessary processing and visual noise.
      clearTimeout(this.recalculatingTimer);
      this.recalculatingTimer = setTimeout(() => {
        this.resetItems();

        if (ViewportHelper.isLargePlus()) {
          this.placeItems();
        }

        this.addScrollingWaypoints();
        this.setFirstYearAsActive();
        this.lastPageWidth = window.innerWidth;
        this.recalculatingTimer = null;
      }, 400);
    },

    /**
     * Recalculate the placement of all items, such as on resize.
     *
     * @param {Boolean} force
     *   Whether or not to force the recalculation, regardless of if the
     *   vertical height has changed. Useful for categorization, etc.
     */
    recalculatePlacements(force = false) {
      // Placement doesn't change on vertical resize, so don't react to it. This
      // also avoids issues when the mobile viewport shifts during scrolling.
      if (this.lastPageWidth === window.innerWidth && !force) {
        return false;
      }

      // Hide all elements if that hasn't already happened. They'll be shown
      // again after they're placed and as they're scrolled to.
      if (!this.recalculatingTimer && !this.animationsDisabled) {
        this.animateTimelineExit();
      }

      this.calculatePlacements();
    },

    /**
     * Places all items in the 2 column layout on larger screens.
     *
     * To make the placement and layout easiest, all items exist in both
     * columns. They are then looped through as left/right pairs. Whichever
     * one is highest (in all cases except featured cards) is made visible and
     * the other one is hidden.
     */
    placeItems() {
      const leftColumn = this.$el.querySelector(SELECTORS.LEFT_COLUMN);
      const rightColumn = this.$el.querySelector(SELECTORS.RIGHT_COLUMN);

      // Items that aren't in the currently selected category will already be
      // hidden and shouldn't be placed, so only grab those that are visible.
      const leftItems = leftColumn.querySelectorAll(`${SELECTORS.ITEM}:not(.${CLASSES.HIDE})`);
      const rightItems = rightColumn.querySelectorAll(`${SELECTORS.ITEM}:not(.${CLASSES.HIDE})`);

      // The offset tracks vertical spacing to ensure items are properly spaced
      // away from those before them to avoid collisions and be consistent.
      let lastOffset = 0;

      // Loop through the items and show/hide 1 item from each pair based on
      // which column they should appear in.
      for (let i = 0; i < rightItems.length; i++) {
        if (i === 1 || this.shouldPlaceItemLeft(leftItems[i], rightItems[i])) {
          lastOffset = this.placeItem(leftItems[i], rightItems[i], lastOffset, i);
        }
        else {
          lastOffset = this.placeItem(rightItems[i], leftItems[i], lastOffset, i);
        }
      }
    },

    /**
     * Places an individual item in the 2 column layout on larger screens.
     *
     * @param {Object} itemToShow - The item that should be shown in its column.
     * @param {Object} itemToHide - The item that should not be shown.
     * @param {Number} lastOffset - The vertical offset of the last placed item.
     * @param {Number} itemNumber - Which item this is among all of them.
     *
     * @return {Number} - The vertical offset of the newly placed item.
     */
    placeItem(itemToShow, itemToHide, lastOffset, itemNumber) {
      const isFeaturedCard = !!itemToShow.querySelector(SELECTORS.CARD_FEATURED);
      const isYear = !!itemToShow.querySelector(SELECTORS.YEAR);

      if (isFeaturedCard) {
        // Featured cards take up both columns, so the "hidden" one is only
        // visually hidden so that it maintains its space in its column, pushing
        // all items after it down.
        itemToHide.style.marginTop = (itemToShow.offsetTop - itemToHide.offsetTop + OFFSET.SPACING) + 'px';
        itemToHide.classList.add(CLASSES.HIDE_VISUALLY);
      }
      else {
        // Items that only take up 1 column and should be hidden are taken out
        // of flow so that everything after them moves up.
        itemToHide.classList.add(CLASSES.HIDE);
      }

      itemToShow.classList.remove(CLASSES.HIDE, CLASSES.HIDE_VISUALLY);

      // Uses the offset to add the vertical spacing that avoids collisions.
      if (itemNumber !== 0) {
        itemToShow.style.marginTop = Math.max(OFFSET.SPACING, lastOffset - itemToShow.offsetTop) + 'px';
      }

      return itemToShow.offsetTop + (isYear ? OFFSET.YEAR : OFFSET.ITEM);
    },

    /**
     * Determines if an item should be shown based on the selected category.
     *
     * @param {Object} element - The element to judge placement for.
     */
    shouldPlaceItem(element) {
      // Only the right column is used on smaller page widths.
      if (!ViewportHelper.isLargePlus() && this.onLeft(element)) {
        return false;
      }

      if (this.category === 'All') {
        return true;
      }

      const yearCategories = element.closest(SELECTORS.GROUP).querySelector(SELECTORS.YEAR).getAttribute(ATTRIBUTES.CATEGORIES).split(',');
      const card = element.querySelector(SELECTORS.CARD);

      // All categories for a given year are stored on the year, so if the year
      // doesn't have it, this element shouldn't be shown.
      if (yearCategories.indexOf(this.category) === -1) {
        return false;
      }

      // If we've made it this far and this item is a year, it should be shown.
      if (element.querySelector(SELECTORS.YEAR)) {
        return true;
      }

      // If the item is a card, determine if the card is in this category.
      if (card && card.getAttribute(ATTRIBUTES.CATEGORIES)) {
        return card.getAttribute(ATTRIBUTES.CATEGORIES).split(',').indexOf(this.category) !== -1;
      }

      return false;
    },

    /**
     * Determines which item should be shown in the 2 column layout.
     *
     * @param {Object} leftItem - The item in the left column.
     * @param {Object} rightItem - The object in the right column.
     *
     * @return {Boolean} - Whether or not it should be shown in the left column.
     */
    shouldPlaceItemLeft(leftItem, rightItem) {
      const isFeaturedCard = !!leftItem.querySelector(SELECTORS.CARD_FEATURED);

      // If the left item is higher than the right, it should be the one that
      // gets shown. That is, unless the item is a featured card, which takes up
      // both columns. The lower of the two should then be shown to avoid
      // colliding with any items in either column above it.
      if (leftItem.offsetTop <= rightItem.offsetTop) {
        return !isFeaturedCard;
      }
      else if (leftItem.offsetTop > rightItem.offsetTop) {
        return isFeaturedCard;
      }
    },

    /**
     * Resets the items to prepare them for placement.
     */
    resetItems() {
      const items = this.$el.querySelectorAll(SELECTORS.ITEM);
      const elements = this.$el.querySelectorAll(`${SELECTORS.CARD}, ${SELECTORS.QUOTE}, ${SELECTORS.YEAR}`);
      const elementsReadable = this.$el.querySelectorAll(`${SELECTORS.CARD}, ${SELECTORS.QUOTE}, ${SELECTORS.CARD_FEATURED_CONTENT}`);

      for (let i = 0; i < items.length; i++) {
        // Remove any previously added placement-specific spacing.
        items[i].style.marginTop = '0px';

        // Hide items that shouldn't be placed and show all others.
        if (!this.shouldPlaceItem(items[i])) {
          items[i].classList.add(CLASSES.HIDE);
        }
        else {
          items[i].classList.remove(CLASSES.HIDE, CLASSES.HIDE_VISUALLY);
        }
      }

      // Remove any styles added by previous waypoint animations.
      for (let i = 0; i < elements.length; i++) {
        elements[i].removeAttribute('style');
      }

      // Make readable to card timeline elements.
      for (let i = 0; i < elementsReadable.length; i++) {
        // Read card content.
        if ((elementsReadable[i].classList.contains(CLASSES.CARD_READABLE) && !ViewportHelper.isMobile()) || elementsReadable[i].classList.contains(CLASSES.QUOTE) ) {
          elementsReadable[i].tabIndex = 0;
        }
        else {
          // Remove tabIndex when not readable or mobile.
          elementsReadable[i].removeAttribute('tabindex');
        }
      }

      this.pauseVideos();
    },

    /**
     * Pauses any Vidyard videos that might be playing.
     */
    pauseVideos() {
      if (typeof VidyardV4 !== 'undefined') {
        const players = VidyardV4.players;

        if (players) {
          players.map(player => {
            if (player.status !== null && player.status.currentTime !== 0) {
              player.pause();
            }
          });
        }
      }
    },

    // Linking and category filtering.

    /**
     * Responds to clicking the anchor link.
     *
     * @param {String} value - The year that was clicked.
     */
    handleAnchorLinkClick(value) {
      if (this.filteredYears.indexOf(value) !== -1) {
        return;
      }

      this.activeYear = value;
      this.scrollToYear(value);
    },

    /**
     * Smooth scrolls the user to a given year.
     *
     * @param {String} year - The year to scroll to.
     */
    scrollToYear(year) {
      // Because larger page widths will have 2 of every item (1 of each is
      // hidden), find the year element that is currently visible.
      const yearElement = this.$el.querySelector(`${SELECTORS.ITEM}:not(.${CLASSES.HIDE}) ${SELECTORS.TIMELINE_YEAR}_${year}`);

      if (yearElement) {
        this.isSmoothScrolling = true;

        // jQuery simplifies element position detection and scroll animations.
        $('html, body').animate({
          scrollTop: $(yearElement).offset().top - OFFSET.YEAR
        }, 500, 'swing', () => {
          this.isSmoothScrolling = false;
        });
      }
    },

    /**
     * Responds to a category change via the dropdown selection.
     *
     * @param {Integer} index - The option index of the selected item.
     * @param {String} value - The value of the selected item.
     */
    handleCategoryChange(index, value) {
      this.category = value;
      this.updateFilteredYears();
      this.recalculatePlacements(true);
    },

    /**
     * Updates the filtered years based on the currently selected category.
     */
    updateFilteredYears() {
      if (this.category === 'All') {
        this.filteredYears = [];
        this.activeYear = this.years[0].toString();
      }
      else {
        const years = this.$el.querySelectorAll(SELECTORS.YEAR);
        let filteredYears = [];

        // Filter out years that don't belong to the current category.
        for (let i = 0; i < years.length; i++) {
          const categories = years[i].getAttribute(ATTRIBUTES.CATEGORIES).split(',');

          if (categories.indexOf(this.category) === -1) {
            filteredYears.push(years[i].getAttribute(ATTRIBUTES.YEAR));
          }
        }

        this.filteredYears = filteredYears;
        this.setFirstYearAsActive();
      }
    },

    /**
     * Sets the first non-filtered year as the active year.
     */
    setFirstYearAsActive() {
      this.activeYear = this.years.filter(year => this.filteredYears.indexOf(year.toString()) === -1)[0].toString();
    },

    // Waypoints.

    /**
     * Adds all waypoints that trigger animations during scroll.
     *
     * @see http://imakewebthings.com/waypoints/
     */
    addScrollingWaypoints() {
      // Waypoint.refreshAll() doesn't work as expected/needed, so it's easier
      // to destroy and then readd them during placement/recalculation.
      Waypoint.destroyAll();

      this.addAnchorLinkWaypoint();
      this.addYearWaypoints();
      this.addCardWaypoints();
      this.addQuoteWaypoints();
    },

    /**
     * Builds an individual waypoint.
     *
     * @param {Object} element - The element to detect scrolling visibility for.
     * @param {Number|String} offset - The trigger point's viewport location.
     * @param {Function} handler - The function to call when it is scrolled to.
     *
     * @return {Object} - The Waypoint object.
     */
    buildWaypoint(element, offset, handler) {
      return new Waypoint({
        element: element,
        offset: offset,
        handler: handler
      });
    },

    /**
     * Add a waypoint to the Anchor Links element so it sticks atop the page.
     */
    addAnchorLinkWaypoint() {
      const content = this.$el.querySelector(SELECTORS.CONTENT);
      const links = this.$el.querySelector(SELECTORS.LINKS);

      if (content && links) {
        const handler = direction => {
          if (direction === 'down') {
            links.classList.add(CLASSES.STICKY);
          }
          else {
            links.classList.remove(CLASSES.STICKY);
          }
        };

        const waypoint = this.buildWaypoint(content, 0, handler);
      }
    },

    /**
     * Add waypoints to all year elements.
     */
    addYearWaypoints() {
      const years = this.$el.querySelectorAll(SELECTORS.YEAR);

      for (let i = 0; i < years.length; i++) {
        // Triggers visual animations when the year becomes visible.
        const entranceHandler = () => {
          if (years[i].closest(`.${CLASSES.HIDE}`)) {
            return entranceWaypoint.destroy();
          }

          if (!this.animationsDisabled) {
            this.animateYearEntrance(years[i]);
          }

          // The animation should only happen once, so destroy the waypoint.
          entranceWaypoint.destroy();
        };

        // Triggers changing the activeYear as the timeline is scrolled.
        const activeHandler = () => {
          if (years[i].closest(`.${CLASSES.HIDE}`)) {
            return activeWaypoint.destroy();
          }

          // This should only occur if the year is visible and isn't being
          // scrolled past as a result of clicking an anchor link up top.
          if (!this.isSmoothScrolling) {
            this.activeYear = years[i].getAttribute(ATTRIBUTES.YEAR);
          }
        };

        const entranceWaypoint = this.buildWaypoint(years[i], '90%', entranceHandler);
        const activeWaypoint = this.buildWaypoint(years[i], OFFSET.YEAR, activeHandler);
      }
    },

    /**
     * Adds waypoints to all card elements.
     */
    addCardWaypoints() {
      const cards = this.$el.querySelectorAll(SELECTORS.CARD);

      for (let i = 0; i < cards.length; i++) {
        const handler = () => {
          if (cards[i].closest(`.${CLASSES.HIDE}`)) {
            return waypoint.destroy();
          }

          if (!this.animationsDisabled) {
            this.animateCardEntrance(cards[i]);
          }

          waypoint.destroy();
        };

        const waypoint = this.buildWaypoint(cards[i], '90%', handler);
      }
    },

    /**
     * Adds waypoints to all quote elements.
     */
    addQuoteWaypoints() {
      const quotes = this.$el.querySelectorAll(SELECTORS.QUOTE);

      for (let i = 0; i < quotes.length; i++) {
        const handler = () => {
          if (quotes[i].closest(`.${CLASSES.HIDE}`)) {
            return waypoint.destroy();
          }

          if (!this.animationsDisabled) {
            this.animateQuoteEntrance(quotes[i]);
          }

          waypoint.destroy();
        };

        const waypoint = this.buildWaypoint(quotes[i], '90%', handler);
      }
    },

    // Animations.

    /**
     * Adds a TweenLite.fromTo tween (reduces repitition below).
     *
     * @param {Object} element - The element to animate.
     * @param {Number} duration - The duration of the animation.
     * @param {Object} options - The From options. All To options are the same.
     */
    addFromTween(element, duration, options) {
      options.ease = "power2.easeOut";
      options.onComplete = () => {
        // Removing the transform fixes offset calculation during placement.
        element.style.transform = '';
      };
      TweenLite.fromTo(element, duration,
        options,
        {
          opacity: 1,
          scale: 1,
          x: 0,
          y: 0
        }
      )
    },

    /**
     * Adds a TweenLite.to tween (reduces repitition below).
     *
     * @param {Object} elements - The elements to animate.
     * @param {Number} duration - The duration of the animation.
     * @param {Object} options - The From options. All To options are the same.
     */
    addToTween(elements, duration, options) {
      options.ease = "power2.easeOut";
      options.onComplete = () => {
        for (let i = 0; i < elements.length; i++) {
          // Removing the transform fixes offset calculation during placement.
          elements[i].style.transform = '';
        }
      };
      TweenLite.to(elements, duration, options);
    },

    /**
     * Determines if the given element is in the left column.
     *
     * The x coordinates of some animations depend on which column they're in.
     *
     * @param {Object} element - The element to determine the location of.
     *
     * @return {Boolean} - Whether or not the element is in the left column.
     */
    onLeft(element) {
      return !!element.closest(SELECTORS.LEFT_COLUMN)
    },

    /**
     * Triggers the exit animation for all items, such as before recalculation.
     */
    animateTimelineExit() {
      this.animateYearExit();
      this.animateCardExit();
      this.animateQuoteExit();
    },

    /**
     * Animates the entrance of a year element.
     *
     * @param {Object} element - The year element to animate.
     */
    animateYearEntrance(element) {
      this.addFromTween(element, .4, {
        opacity: 0,
        scale: 0
      });
    },

    /**
     * Animates the exit of all year elements, such as before recalculation.
     */
    animateYearExit() {
      const elements = this.$el.querySelectorAll(SELECTORS.YEAR);

      this.addToTween(elements, .4, {
        opacity: 0,
        scale: 0
      });
      // Resets the scale for when it is shown again later.
      this.addToTween(elements, 0, {
        delay: .4,
        scale: 1
      });
    },

    /**
     * Animates the entrance of a card element.
     *
     * @param {Object} element - The card element to animate.
     */
    animateCardEntrance(element) {
      // Card animations vary based on the page width and featured vs not.
      if (ViewportHelper.isLargePlus() && element.classList.contains(CLASSES.CARD_FEATURED)) {
        this.animateFeaturedCardEntranceLgPlus(element);
      }
      else if (ViewportHelper.isTablet()) {
        this.animateCardEntranceMdUp(element);
      }
      else {
        this.addFromTween(element, .6, {
          opacity: 0,
          x: 0,
          y: ANIMATION.DISTANCE_SM
        });
      }
    },

    /**
     * Animates the entrance of a card element on larger page widths.
     *
     * @param {Object} element - The card element to animate.
     */
    animateCardEntranceMdUp(element) {
      const onLeft = this.onLeft(element);
      const dot = element.closest(SELECTORS.TIMELINE_CARD).querySelector(SELECTORS.ARROW);
      const arrow = element.closest(SELECTORS.TIMELINE_CARD).querySelector(SELECTORS.ARROW_ICON);
      const fromX = (onLeft ? 1 : -1) * ANIMATION.DISTANCE_MD;

      // The dots, arrows, and cards are all animated independently.
      TweenLite.set(arrow, {
        rotation: onLeft ? 180 : 0,
        onComplete: () => {
          this.addFromTween(dot, .15, {
            opacity: 0,
            scale: 0
          });
          this.addFromTween(arrow, .2, {
            delay: .15,
            opacity: 0,
            x: fromX,
            y: 0
          });
          this.addFromTween(element, .6, {
            delay: .25,
            opacity: 0,
            x: fromX,
            y: 0
          });
        }
      })
    },

    /**
     * Animates the entrance of a featured card element on larger page widths.
     *
     * @param {Object} element - The featured card element to animate.
     */
    animateFeaturedCardEntranceLgPlus(element) {
      const cardContent = element.querySelector(SELECTORS.CARD_CONTENT);

      this.addFromTween(element, .2, {
        opacity: 0
      });
      this.addFromTween(cardContent, .6, {
        opacity: 0,
        x: 0,
        y: ANIMATION.DISTANCE_MD
      });
    },

    /**
     * Animates the exit of all card elements.
     */
    animateCardExit() {
      if (ViewportHelper.isTablet()) {
        const elements = this.$el.querySelectorAll(SELECTORS.TIMELINE_CARD);

        this.animateCardExitMdUp(elements);
      }
      else {
        const elements = this.$el.querySelectorAll(SELECTORS.CARD);

        this.addToTween(elements, .4, {
          opacity: 0,
          y: ANIMATION.DISTANCE_SM
        });
      }
    },

    /**
     * Animates the exit of all card elements on larger page widths.
     */
    animateCardExitMdUp(elements) {
      const dots = [];
      const arrows = {
        'left': [],
        'right': []
      };
      const cards = {
        'left': [],
        'right': []
      };
      const leftOptions = {
        delay: .1,
        opacity: 0,
        x: ANIMATION.DISTANCE_MD
      };
      const rightOptions = {
        delay: .1,
        opacity: 0,
        x: -1 * ANIMATION.DISTANCE_MD
      };

      for (let i = 0; i < elements.length; i++) {
        const side = this.onLeft(elements[i]) ? 'left' : 'right';

        dots.push(elements[i].querySelector(SELECTORS.ARROW));
        arrows[side].push(elements[i].querySelector(SELECTORS.ARROW_ICON));
        cards[side].push(elements[i].querySelector(SELECTORS.CARD));
      }

      // The dots, arrows, and cards are all animated independently.
      this.addToTween(dots, .1, {
        opacity: 0,
        scale: 0
      });
      this.addToTween(arrows['left'], .15, leftOptions);
      this.addToTween(arrows['right'], .3, rightOptions);
      this.addToTween(cards['left'], .15, leftOptions);
      this.addToTween(cards['right'], .3, rightOptions);
    },

    /**
     * Animates the entrance of a quote element.
     *
     * @param {Object} element - The quote element to animate.
     */
    animateQuoteEntrance(element) {
      if (ViewportHelper.isTablet()) {
        this.animateQuoteEntranceMdUp(element);
      }
      else {
        this.addFromTween(element, .6, {
          opacity: 0,
          x: 0,
          y: ANIMATION.DISTANCE_SM
        });
      }
    },

    /**
     * Animates the entrance of a quote element on larger page widths.
     *
     * @param {Object} element - The quote element to animate.
     */
    animateQuoteEntranceMdUp(element) {
      this.addFromTween(element, .6, {
        opacity: 0,
        x: (this.onLeft(element) ? 1 : -1) * ANIMATION.DISTANCE_MD,
        y: 0
      });
    },

    /**
     * Animates the exit of all quote elements.
     */
    animateQuoteExit() {
      const elements = this.$el.querySelectorAll(SELECTORS.QUOTE);

      if (ViewportHelper.isTablet()) {
        this.animateQuoteExitMdUp(elements);
      }
      else {
        this.addToTween(elements, .4, {
          delay: .1,
          opacity: 0,
          y: ANIMATION.DISTANCE_SM
        });
      }
    },

    /**
     * Animates the exit of all quote elements on larger page widths.
     */
    animateQuoteExitMdUp(elements) {
      const leftElements = []
      const rightElements = [];

      for (let i = 0; i < elements.length; i++) {
        if (this.onLeft(elements[i])) {
          leftElements.push(elements[i]);
        }
        else {
          rightElements.push(elements[i]);
        }
      }

      this.addToTween(leftElements, .4, {
        opacity: 0,
        x: ANIMATION.DISTANCE_MD
      });
      this.addToTween(elements, .4, {
        opacity: 0,
        x: -1 * ANIMATION.DISTANCE_MD
      });
    }
  }
})
